import { Content, Markdown } from '@/lib/markdown';
import Button from './Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/pro-regular-svg-icons';
import { ctfImage } from '@/lib/contentful-url';

export interface ICallToAction {
	className?: string;
	caption?: string;
	title?: string;
	description?: Content;
	iconList?: {
		iconName: string;
		text?: string;
	}[];
	buttons?: {
		color?: 'dark' | 'primary' | 'neutral';
		iconName?: string;
		text: string;
		link: string;
	}[];
	image: {
		url: string;
		title: string;
		width?: number;
		height?: number;
	};
	imageCover?: 'contain' | 'cover';
}

export default function CallToAction(props: ICallToAction) {
	return (
		<section
			className={`call-to-action container mx-auto flex flex-col items-center justify-center text-center  relative ${
				!!props.className && props.className
			}`}
		>
			<div className="flex flex-col gap-4 justify-center mx-auto max-w-5xl  items-center">
				<div>
					{!!props.caption?.length ? (
						<p className="font-bold text-primary-500 uppercase text-xs">
							{props.caption}
						</p>
					) : null}
					{!!props.title?.length ? (
						<h2 className="text-3xl md:text-5xl font-extrabold">{props.title}</h2>
					) : null}
				</div>
				{!!props.description ? (
					<Markdown
						className="w-full md:w-[75%] [&_p]:xl:text-2xl [&_p]:lg:text-xl [&_p]:text-lg "
						content={props?.description}
					/>
				) : null}
				{!!props.iconList?.length ? (
					<div className="flex flex-col gap-4">
						{props.iconList.map((icon, index) => {
							return (
								<div key={index} className="flex items-center gap-4">
									<FontAwesomeIcon
										className="text-primary-500 size-5"
										icon={['far', icon.iconName as IconName]}
									/>
									<p className="text-sm font-normal">{icon.text}</p>
								</div>
							);
						})}
					</div>
				) : null}
				{!!props.buttons?.length ? (
					<div className="flex items-center justify-center gap-4 mt-2 lg:mt-8">
						{props.buttons.map((button, index) => {
							return (
								<Button
									color={button.color}
									startIcon={
										button.iconName && (
											<FontAwesomeIcon
												icon={['far', button.iconName as IconName]}
											/>
										)
									}
									key={index}
									href={button.link}
								>
									{button.text}
								</Button>
							);
						})}
					</div>
				) : null}
			</div>

			{!!props.image ? (
				<div className="col-span-12 z-40 md:col-span-6 flex flex-col gap-4 order-1 mt-8 lg:mt-16 z-10 ">
					{/* biome-ignore lint/performance/noImgElement: static export disables next/image optimization, so responsive variants must come from Contentful URL params via srcSet */}
					<img
						src={ctfImage(props.image.url, { width: 1600, quality: 70 })}
						srcSet={[480, 800, 1200, 1600]
							.map(
								(w) =>
									`${ctfImage(props.image.url, { width: w, quality: 70 })} ${w}w`
							)
							.join(', ')}
						sizes="(min-width: 1280px) 1216px, 100vw"
						alt={props.image.title}
						loading="lazy"
						decoding="async"
						width={props.image.width ?? 500}
						height={props.image.height ?? 500}
						className={`w-full z-30  relative h-full max-h-[400px] max-w-full md:max-h-full ${
							props.imageCover === 'cover' ? 'object-cover' : 'object-contain'
						}`}
					/>
				</div>
			) : null}
		</section>
	);
}
